import { ComboboxProps, Select, SelectOption } from '@chronosphereio/chrono-ui';
import { useFeatureFlags, useTextWidth } from '@chronosphereio/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { VariableOption } from '../../../core';
import { useExtraOption } from './useExtraSelectOption';
import { VariableCombobox } from './VariableCombobox';
import { INPUT_MAX_WIDTH, INPUT_MIN_WIDTH, useTemplateVariableOptions } from './variable-options';

export interface SingleSelectVariableProps {
  title: string;
  options?: VariableOption[];
  isLoading: boolean;
  allowAllValue: boolean;
  value: string | null;
  hasLoaded: boolean;
  onChange: (nextValue: string) => void;
  disabled?: boolean;
  renderFooter?: ComboboxProps['renderFooter'];
}

/**
 * Input for selecting a single variable value from a list of options.
 */
export function SingleSelectVariable(props: SingleSelectVariableProps) {
  const { enableNewVariableUx: enableNewTemplateVariables } = useFeatureFlags();
  const { title, options, isLoading, allowAllValue, value, onChange, disabled } = props;

  const { optionsByKey, popperMinWidth, selectOptions, hasLoadedOptions } = useTemplateVariableOptions(
    allowAllValue,
    false,
    options
  );
  const [inputWidth, setInputWidth] = useState(0);

  const selectValue = useMemo(() => {
    // If the value is null (not sure when this could happen?), return a dummy placeholder option so that we don't pass
    // undefined and switch the input between being uncontrolled and controlled
    if (value === null) return NO_VALUE;

    const found = optionsByKey.get(value);
    if (found !== undefined) return found;

    // If not found, return a dummy placeholder option based on the value (TODO: Is this what we want?)
    return { key: value, label: value };
  }, [optionsByKey, value]);

  useEffect(() => {
    // Don't change values while loading
    if (isLoading) return;

    // If we don't have a selection, but we have options, select the first option
    if (selectValue === NO_VALUE && selectOptions.length > 0 && selectOptions[0] !== undefined) {
      onChange(selectOptions[0].key);
    }
  }, [selectOptions, selectValue, onChange, isLoading, hasLoadedOptions]);

  // Set the popper width to whichever is larger: the width of the input or the width of the largest item
  // in the dropdown (popperMinWidth). This ensures that the popper's width is at least the minimum width,
  // but can grow to match the input element's width if it is larger.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current !== null) {
      setInputWidth(inputRef.current.offsetWidth);
    }
  }, [selectValue]);
  const popperWidth = Math.max(inputWidth, popperMinWidth);

  const selectedValueWidth = useTextWidth(selectValue.label);

  const { onInputChangeHandler, optionsWithExtra, filterOptions } = useExtraOption(selectOptions);

  const comboboxOptions = useMemo(() => {
    return selectOptions.map((d) => ({
      label: d.label,
      value: d.key,
    }));
  }, [selectOptions]);
  return (
    <>
      {!enableNewTemplateVariables && (
        <Select
          Label={title}
          filterOptions={filterOptions}
          disableClearable
          options={optionsWithExtra}
          loading={isLoading}
          disabled={disabled || isLoading}
          value={selectValue}
          onChange={(e, next) => {
            // Don't allow clearing (TODO: disableClearable should already be preventing this so the onChange signature
            // should reflect that?)
            if (next === null) return;
            onChange(next.key);
          }}
          sx={{
            minWidth: INPUT_MIN_WIDTH,
            maxWidth: INPUT_MAX_WIDTH,
            '.MuiOutlinedInput-root .MuiAutocomplete-input': {
              width: selectedValueWidth,
            },
          }}
          popperWidth={`${popperWidth}px`}
          InputProps={{ ref: inputRef, onChange: onInputChangeHandler }}
        />
      )}
      {enableNewTemplateVariables && (
        <VariableCombobox
          label={title}
          options={comboboxOptions}
          isLoading={isLoading}
          disabled={disabled}
          value={selectValue !== NO_VALUE ? selectValue.key : null}
          onSelect={(e) => {
            onChange(e as unknown as string);
          }}
          renderFooter={props.renderFooter}
        />
      )}
    </>
  );
}

const NO_VALUE: SelectOption = { key: '__null__', label: '' };
